/* =====================================================================
   Shipping protection toggle (cart drawer + cart page).
   - The shopper's choice is stored as a cart attribute (_pv_protect: on/off),
     so the protection never shows up as a cart item or in the cart count.
   - Pressing Checkout with protection on adds the product, then goes to
     checkout. If the shopper comes back to the cart, the line is taken back
     out so the cart stays clean (the attribute keeps their choice).
   ===================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let busy = false;
let failed = false;

const boxes = () => Array.from(document.querySelectorAll('[data-pv-protect]'));
const firstBox = () => boxes().find((b) => !b.hidden) || boxes()[0] || null;

async function post(path, body) {
  const res = await fetch(ROOT + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  if (document.getElementById('shopify-section-cart-drawer-section')) ids.add('cart-drawer-section');
  return Array.from(ids);
}

async function renderSections(sections) {
  for (const [id, html] of Object.entries(sections || {})) {
    if (!html) continue;
    try {
      await morphSection(id, html, { mode: id === 'cart-drawer-section' ? 'hydration' : 'full' });
    } catch (e) {
      /* section not on this page */
    }
  }
}

/* take the protection line back out (shopper returned from checkout) */
async function removeLine(key) {
  busy = true;
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: [{ id: key, quantity: 0 }], promise: deferred.promise })
  );
  try {
    const cart = await post('cart/change.js', {
      id: key,
      quantity: 0,
      sections: cartSectionIds().join(','),
      sections_url: location.pathname,
    });
    if (cart.errors || cart.status) throw new Error(cart.description || cart.errors || 'cart error');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'pv-protect', didError: false },
    });
    await renderSections(cart.sections);
  } catch (e) {
    deferred.reject(e);
    failed = true;
  }
  busy = false;
}

function check() {
  if (busy || failed) return;
  const b = boxes().find((x) => x.dataset.todo === 'remove' && x.dataset.key);
  if (b) removeLine(b.dataset.key);
}

/* the switch */
async function setChoice(on) {
  boxes().forEach((b) => {
    b.dataset.on = on ? '1' : '0';
    b.classList.toggle('is-on', on);
    const sw = b.querySelector('[role="switch"]');
    if (sw) sw.setAttribute('aria-checked', String(on));
  });
  try {
    await post('cart/update.js', { attributes: { _pv_protect: on ? 'on' : 'off' } });
  } catch (e) {
    /* choice still applies for this visit */
  }
}

document.addEventListener('click', (e) => {
  const sw = e.target.closest('[data-pv-protect] [role="switch"]');
  if (!sw) return;
  e.preventDefault();
  setChoice(sw.getAttribute('aria-checked') !== 'true');
});

/* checkout: add protection first when it's on */
document.addEventListener(
  'click',
  async (e) => {
    const btn = e.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
    if (!btn || btn.dataset.pvGo) return;
    const b = firstBox();
    if (!b || b.hidden || b.dataset.on !== '1' || b.dataset.key) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    btn.dataset.pvGo = '1';
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('pv-going');
    try {
      await post('cart/add.js', { items: [{ id: Number(b.dataset.variant), quantity: 1 }] });
    } catch (err) {
      /* checkout anyway rather than block the order */
    }
    location.href = ROOT + 'checkout';
  },
  true
);

/* coming back from checkout (incl. back/forward cache) */
window.addEventListener('pageshow', (e) => {
  const went = document.querySelectorAll('[data-pv-go]');
  went.forEach((btn) => {
    delete btn.dataset.pvGo;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('pv-going');
  });
  /* restored from the back/forward cache after we sent them to checkout: refresh so the line gets removed */
  if (e.persisted && went.length) location.reload();
});

let t = 0;
new MutationObserver(() => {
  clearTimeout(t);
  t = setTimeout(check, 60);
}).observe(document.body, { childList: true, subtree: true });
check();
